// src/Pages/ProjectPage.js
// Theme-driven case-study page for a single project. Config lives in src/data/projects.js;
// each project supplies its own colours, fonts, and hero layout variant so the page takes on
// that project's identity. Galleries render only when real images exist.
import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { PROJECTS, ALL } from '../data/projects';

const STYLES = `
.pj-root{position:relative;overflow-x:hidden;min-height:100vh;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;}
.pj-root *{box-sizing:border-box;}
.pj-root a{color:var(--ink);text-decoration:none;transition:color .25s ease;}
.pj-root a:hover{color:var(--accent);}
.pj-root ::selection{background:var(--accent);color:var(--bg);}
html{scroll-behavior:smooth;}

.pj-r{opacity:0;transform:translateY(22px);transition:opacity .7s ease,transform .7s ease;}
.pj-r.in{opacity:1;transform:none;}

.pj-bar{position:fixed;top:0;left:0;right:0;z-index:100;display:flex;align-items:center;justify-content:space-between;
  gap:14px;padding:16px clamp(20px,5vw,72px);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border-bottom:1px solid var(--hair);}

.pj-media{position:absolute;inset:0;transition:transform .6s cubic-bezier(.2,.7,.2,1);}
.pj-media img{width:100%;height:100%;object-fit:cover;display:block;}
.pj-g{position:relative;overflow:hidden;}
.pj-g:hover .pj-media{transform:scale(1.05);}

.pj-2col{display:grid;grid-template-columns:.4fr .6fr;gap:clamp(30px,5vw,80px);}
.pj-ba-grid{display:grid;grid-template-columns:1fr 1fr;gap:clamp(14px,2vw,28px);}
.pj-split{display:grid;grid-template-columns:1.05fr .95fr;gap:clamp(30px,5vw,64px);align-items:center;}
.pj-edit-brief{display:grid;grid-template-columns:1fr 1fr;gap:clamp(24px,4vw,64px);align-items:start;}
.pj-gallery{display:grid;grid-template-columns:repeat(auto-fit,minmax(clamp(240px,30vw,340px),1fr));grid-auto-rows:300px;gap:clamp(14px,1.6vw,22px);}

.pj-next:hover .pj-next-arrow{transform:translateX(10px);}
.pj-next-arrow{transition:transform .35s ease;}

@media (max-width:820px){
  .pj-2col,.pj-split,.pj-edit-brief{grid-template-columns:1fr;gap:20px;}
  .pj-ba-grid{grid-template-columns:1fr;}
  .pj-gallery{grid-template-columns:1fr;grid-auto-rows:280px;}
  .pj-bar-mid{display:none;}
}
@media (prefers-reduced-motion:reduce){
  html{scroll-behavior:auto;}
  .pj-r{opacity:1;transform:none;transition:none;}
  .pj-media{transition:none;}
}
`;

function segStyle(s, t) {
  if (s.gradient) {
    return { backgroundImage: t.titleGradient, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', fontStyle: s.italic ? 'italic' : 'normal' };
  }
  return { color: s.color || (s.accent ? 'var(--accent)' : 'inherit'), fontStyle: s.italic ? 'italic' : 'normal' };
}

export default function ProjectPage() {
  const { slug } = useParams();
  const project = PROJECTS[slug];
  const rootRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll('.pj-r');
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }),
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach((el) => io.observe(el));
    const fb = setTimeout(() => els.forEach((el) => el.classList.add('in')), 4000);
    return () => { io.disconnect(); clearTimeout(fb); };
  }, [slug]);

  if (!project) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: '#FBF6EF', color: '#1E1811', fontFamily: 'Manrope, system-ui, sans-serif' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', margin: 0 }}>Project not found</h1>
        <a href="/#work" style={{ color: '#E24D8F' }}>← Back to all work</a>
      </div>
    );
  }

  const t = project.theme;
  const img = project.images;
  const variant = project.heroVariant || 'standard';
  const idx = ALL.findIndex((p) => p.slug === slug);
  const num = String(idx + 1).padStart(2, '0');
  const total = String(ALL.length).padStart(2, '0');

  let next = null;
  for (let i = 1; i <= ALL.length; i++) {
    const cand = ALL[(idx + i) % ALL.length];
    if (cand.slug !== slug && PROJECTS[cand.slug]) { next = cand; break; }
  }

  const rootStyle = {
    '--bg': t.bg, '--ink': t.ink, '--body': t.body, '--muted': t.muted,
    '--accent': t.accent, '--accentText': t.accentText, '--hair': t.hair, '--barbg': t.barBg,
    background: t.bg, color: t.ink, fontFamily: t.bodyFont,
  };
  const pad = 'clamp(20px,5vw,72px)';
  const metaFont = { fontFamily: t.mono || t.bodyFont };
  const overline = { ...metaFont, fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: t.mono ? 400 : 700 };
  const eyebrowColor = project.eyebrowColor || 'var(--accent)';

  const titleEl = (align) => (
    <h1 className="pj-r" style={{ margin: 0, fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: project.titleSize || 'clamp(58px,12vw,176px)', lineHeight: 0.92, letterSpacing: '-.02em', textAlign: align || 'left', transitionDelay: '.05s' }}>
      {project.title.map((s, i) => <span key={i} style={segStyle(s, t)}>{s.t}</span>)}
    </h1>
  );

  const eyebrowEl = (mb) => (
    <div className="pj-r" style={{ ...metaFont, fontSize: 12, letterSpacing: t.eyebrowLS || '.2em', textTransform: 'uppercase', color: eyebrowColor, fontWeight: t.mono ? 400 : 700, display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: mb }}>
      {project.eyebrowDot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: eyebrowColor }} />}
      {project.eyebrow}
    </div>
  );

  const metaEl = () => (project.meta ? (
    <div className="pj-r" style={{ display: 'flex', gap: 34, flexWrap: 'wrap', transitionDelay: '.18s' }}>
      {project.meta.map(([k, v]) => (
        <div key={k}>
          <div style={{ ...overline, marginBottom: 8 }}>{k}</div>
          <div style={{ ...metaFont, fontSize: 14, fontWeight: 600 }}>{v}</div>
        </div>
      ))}
    </div>
  ) : null);

  const heroImage = (aspect, extra = {}) => (img.hero ? (
    <div className="pj-r" style={{ marginTop: extra.mt != null ? extra.mt : 56, maxWidth: extra.maxWidth, marginLeft: extra.center ? 'auto' : undefined, marginRight: extra.center ? 'auto' : undefined, aspectRatio: aspect, borderRadius: t.radius, overflow: 'hidden', border: extra.noBorder ? 'none' : '1px solid var(--hair)', boxShadow: extra.shadow, transitionDelay: '.24s' }}>
      <img src={img.hero} alt={`${ALL[idx].name} — hero`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: project.heroPosition || 'center', display: 'block' }} />
    </div>
  ) : null);

  const introSans = { margin: 0, maxWidth: 520, fontSize: 'clamp(16px,1.6vw,20px)', lineHeight: 1.6, color: 'var(--body)', transitionDelay: '.12s' };
  const introSerif = { fontFamily: t.titleFont, fontStyle: 'italic', fontSize: 'clamp(20px,2.2vw,28px)', lineHeight: 1.5, color: 'var(--body)' };

  // ---------- HERO (per variant) ----------
  let hero;
  if (variant === 'standard') {
    hero = (
      <div style={{ position: 'relative', maxWidth: 1200, margin: '0 auto' }}>
        {eyebrowEl(26)}
        {titleEl('left')}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 40 }}>
          <p className="pj-r" style={introSans}>{project.intro}</p>
          {metaEl()}
        </div>
        {heroImage(project.heroAspect || '16 / 8')}
      </div>
    );
  } else if (variant === 'centered') {
    hero = (
      <div style={{ textAlign: 'center' }}>
        <div style={{ position: 'relative', maxWidth: 1100, margin: '0 auto' }}>
          {eyebrowEl(34)}
          {titleEl('center')}
          <p className="pj-r" style={{ ...introSerif, margin: '36px auto 0', maxWidth: 600, transitionDelay: '.14s' }}>{project.intro}</p>
        </div>
        {heroImage(project.heroAspect || '16 / 8', { mt: 60, maxWidth: 1200, center: true })}
      </div>
    );
  } else if (variant === 'split') {
    hero = (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="pj-split">
          <div>
            {eyebrowEl(22)}
            {titleEl('left')}
            <p className="pj-r" style={{ ...introSans, maxWidth: 460, margin: '24px 0 32px' }}>{project.intro}</p>
            {metaEl()}
          </div>
          <div className="pj-r" style={{ aspectRatio: project.heroAspect || '4 / 5', borderRadius: t.radius, overflow: 'hidden', boxShadow: '0 30px 60px -30px rgba(14,27,43,.3)', transitionDelay: '.12s' }}>
            <img src={img.hero} alt={`${ALL[idx].name} — hero`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: project.heroPosition || 'center', display: 'block' }} />
          </div>
        </div>
      </div>
    );
  } else if (variant === 'editorial') {
    hero = (
      <div>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="pj-r" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--ink)', paddingBottom: 20, flexWrap: 'wrap', gap: 12 }}>
            {(project.editorialLabels || []).map((l, i) => (
              <span key={i} style={{ ...metaFont, fontSize: 11, letterSpacing: '.28em', textTransform: 'uppercase', color: 'var(--muted)' }}>{l}</span>
            ))}
          </div>
          <h1 className="pj-r" style={{ margin: '36px 0 0', fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: project.titleSize || 'clamp(64px,16vw,240px)', lineHeight: 0.86, letterSpacing: '-.01em', textAlign: 'center', transitionDelay: '.06s' }}>
            {project.title.map((s, i) => <span key={i} style={segStyle(s, t)}>{s.t}</span>)}
          </h1>
        </div>
        <div className="pj-r" style={{ maxWidth: 1280, margin: '44px auto 0', aspectRatio: project.heroAspect || '16 / 9', overflow: 'hidden', transitionDelay: '.14s' }}>
          <img src={img.hero} alt={`${ALL[idx].name} — hero`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: project.heroPosition || 'center', display: 'block' }} />
        </div>
        <p className="pj-r" style={{ ...introSerif, maxWidth: 640, margin: '40px auto 0', textAlign: 'center', transitionDelay: '.2s' }}>{project.intro}</p>
      </div>
    );
  } else if (variant === 'social') {
    hero = (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="pj-r" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 26 }}>
          {(project.tags || []).map((tag) => (
            <span key={tag.label} style={{ padding: '7px 16px', borderRadius: 999, background: tag.bg, color: '#fff', fontSize: 12, fontWeight: 700 }}>{tag.label}</span>
          ))}
        </div>
        {titleEl('left')}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 40, justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 34 }}>
          <p className="pj-r" style={introSans}>{project.intro}</p>
          {metaEl()}
        </div>
      </div>
    );
  }

  // ---------- BRIEF ----------
  const briefBlock = variant === 'editorial' ? (
    <section style={{ position: 'relative', padding: `clamp(60px,8vw,120px) ${pad}` }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <div className="pj-r" style={{ ...metaFont, fontSize: 11, letterSpacing: '.28em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 28 }}>{project.briefLabel || 'The Brief'}</div>
        <div className="pj-r pj-edit-brief" style={{ transitionDelay: '.08s' }}>
          <h2 style={{ margin: 0, fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: 'clamp(28px,3.6vw,50px)', lineHeight: 1.05 }}>{project.briefHeading}</h2>
          <div>
            {project.brief.map((p, i) => (
              <p key={i} style={{ margin: i === project.brief.length - 1 ? 0 : '0 0 20px', fontSize: 'clamp(15px,1.3vw,17px)', lineHeight: 1.85, color: 'var(--body)' }}>{p}</p>
            ))}
          </div>
        </div>
      </div>
    </section>
  ) : (
    <section style={{ position: 'relative', padding: `clamp(60px,8vw,120px) ${pad}` }}>
      <div className="pj-2col" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="pj-r" style={{ ...metaFont, fontSize: 13, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--accent)', fontWeight: t.mono ? 400 : 700 }}>{project.briefLabel || 'The Brief'}</div>
        <div className="pj-r" style={{ transitionDelay: '.08s' }}>
          <h2 style={{ margin: '0 0 26px', fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: 'clamp(28px,3.5vw,46px)', lineHeight: 1.08, letterSpacing: '-.01em' }}>{project.briefHeading}</h2>
          {project.brief.map((p, i) => (
            <p key={i} style={{ margin: i === project.brief.length - 1 ? 0 : '0 0 20px', fontSize: 'clamp(16px,1.4vw,18px)', lineHeight: 1.75, color: 'var(--body)' }}>{p}</p>
          ))}
        </div>
      </div>
    </section>
  );

  const galleryLabel = project.galleryLabel || 'Selected deliverables';

  return (
    <>
      <Helmet>
        <title>{ALL[idx].name} — Anita Eboh</title>
        <meta name="description" content={`${ALL[idx].name} — a project by brand & graphic designer Anita Eboh.`} />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href={t.fonts} rel="stylesheet" />
      </Helmet>

      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      <div className="pj-root" ref={rootRef} style={rootStyle}>
        {/* top bar */}
        <div className="pj-bar" style={{ background: 'var(--barbg)' }}>
          <a href="/#work" style={{ ...metaFont, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--accent)' }}>←</span> All work
          </a>
          <div className="pj-bar-mid" style={{ ...metaFont, fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--muted)' }}>
            {ALL[idx].name} — {num} / {total}
          </div>
          {next ? (
            <Link to={`/work/${next.slug}`} style={{ ...metaFont, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Next <span style={{ color: 'var(--accent)' }}>→</span>
            </Link>
          ) : (
            <a href="/#work" style={{ ...metaFont, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              All work <span style={{ color: 'var(--accent)' }}>→</span>
            </a>
          )}
        </div>

        {/* hero */}
        <section style={{ position: 'relative', padding: `130px ${pad} 60px`, overflow: 'hidden' }}>
          {t.glows.map((g, i) => (
            <div key={i} style={{ position: 'absolute', top: g.top, right: g.right, bottom: g.bottom, left: g.left, width: `min(${g.size}px,60vw)`, height: `min(${g.size}px,60vw)`, background: `radial-gradient(circle,${g.color},transparent 68%)`, opacity: g.o, filter: 'blur(88px)', pointerEvents: 'none' }} />
          ))}
          <div style={{ position: 'relative' }}>{hero}</div>
        </section>

        {/* before / after (Mix Lab) */}
        {img.before && img.after && (
          <section style={{ position: 'relative', padding: `40px ${pad}` }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              <div className="pj-r pj-ba-grid">
                <div>
                  <div style={{ ...overline, marginBottom: 14 }}>Before</div>
                  <div style={{ aspectRatio: '4 / 3', borderRadius: t.radius, overflow: 'hidden', border: '1px solid var(--hair)', filter: 'grayscale(.35)' }}>
                    <img src={img.before} alt="Before" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                </div>
                <div>
                  <div style={{ ...overline, color: 'var(--accent)', marginBottom: 14 }}>After</div>
                  <div style={{ aspectRatio: '4 / 3', borderRadius: t.radius, overflow: 'hidden', border: '1px solid var(--accent)', boxShadow: '0 0 0 1px var(--accent)' }}>
                    <img src={img.after} alt="After" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* brief */}
        {briefBlock}

        {/* gallery */}
        {img.gallery && img.gallery.length > 0 && (
          <section style={{ position: 'relative', padding: `0 ${pad} clamp(70px,9vw,130px)` }}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
              {variant === 'editorial' ? (
                <div className="pj-r" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderBottom: '1px solid var(--ink)', paddingBottom: 16, marginBottom: 'clamp(24px,3vw,40px)' }}>
                  <h3 style={{ margin: 0, fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: 'clamp(22px,2.6vw,34px)' }}>{galleryLabel}</h3>
                  <span style={{ ...metaFont, fontSize: 11, letterSpacing: '.28em', textTransform: 'uppercase', color: 'var(--muted)' }}>02</span>
                </div>
              ) : (
                <h3 className="pj-r" style={{ margin: '0 0 clamp(24px,3vw,40px)', fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: 'clamp(22px,2.5vw,32px)', letterSpacing: '-.01em' }}>{galleryLabel}</h3>
              )}
              <div className="pj-gallery">
                {img.gallery.map((g, i) => (
                  <div key={i} className="pj-r pj-g" style={{ borderRadius: t.radius, transitionDelay: `${(i % 3) * 0.05}s` }}>
                    <div className="pj-media"><img src={g.src} alt={g.alt || `${ALL[idx].name} deliverable ${i + 1}`} loading="lazy" /></div>
                    {g.tag && (
                      <span style={{ position: 'absolute', top: 14, left: 14, padding: '6px 13px', borderRadius: 999, background: g.tag.bg, color: g.tag.fg || '#fff', fontSize: 11, fontWeight: 700, pointerEvents: 'none' }}>{g.tag.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* next project */}
        {next && (
          <Link className="pj-next" to={`/work/${next.slug}`} style={{ display: 'block', borderTop: '1px solid var(--hair)' }}>
            <section style={{ position: 'relative', padding: `clamp(56px,7vw,100px) ${pad}`, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
              <div>
                <div style={{ ...overline, marginBottom: 14 }}>Next project</div>
                <div style={{ fontFamily: t.titleFont, fontWeight: t.titleWeight, fontSize: 'clamp(36px,6vw,84px)', lineHeight: 1, letterSpacing: '-.02em' }}>{next.name}</div>
              </div>
              <div className="pj-next-arrow" style={{ fontSize: 'clamp(40px,6vw,80px)', color: 'var(--accent)' }}>→</div>
            </section>
          </Link>
        )}

        {/* footer */}
        <div style={{ padding: `26px ${pad}`, borderTop: '1px solid var(--hair)', ...metaFont, fontSize: 12, color: 'var(--muted)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <span>© 2026 Anita Eboh</span>
          <a href="/#contact">Let&rsquo;s work together</a>
        </div>
      </div>
    </>
  );
}
